import type { InvestorData, PitchSessionData } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import { Banner } from '@workspace/ui/components/banner'
import { Button } from '@workspace/ui/components/button'
import { EmptyState } from '@workspace/ui/components/empty-state'
import { ErrorBanner } from '@workspace/ui/components/error-banner'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { ProfileSection } from '@workspace/ui/components/profile-section'
import { Switch } from '@workspace/ui/components/switch'
import { Building2, Check, Copy, ExternalLink, Mic, Share2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { PitchTimeline } from '@/features/pitch/components/pitch-timeline'
import { useInvestorQuery, usePitchesQuery, useUpdateInvestorMutation } from '@/hooks/api'

export function InvestorPitchesContent() {
  const { data: investorResponse, isLoading: investorLoading } = useInvestorQuery()
  const investor = investorResponse?.data

  if (investorLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="md" />
      </div>
    )
  }

  if (!investor) {
    return <ErrorBanner message="No investor profile found" />
  }

  return <InvestorPitchTimeline investor={investor} />
}

function InvestorLinkBanner({ investor }: { investor: InvestorData }) {
  const [isEditing, setIsEditing] = useState(false)
  const [slugInput, setSlugInput] = useState(investor.slug ?? '')
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const shareUrl = `${window.location.origin}/${investor.slug}`
  const isAccepting = investor.isAcceptingPitches
  const variant = isAccepting ? 'success' : 'primary'
  const borderColor = isAccepting ? 'border-emerald-500/20' : 'border-[#f97316]/20'

  const { mutate: updateInvestor, isPending } = useUpdateInvestorMutation({
    onSuccess: () => {
      setError(null)
      setIsEditing(false)
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to update link')
    },
  })

  useEffect(() => {
    if (investor.slug) {
      setSlugInput(investor.slug)
    }
  }, [investor.slug])

  const handleEdit = () => {
    setIsEditing(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const handleSave = () => {
    const normalized = slugInput.toLowerCase().trim()
    if (normalized.length < 3) {
      setError('Link must be at least 3 characters')
      return
    }
    if (!/^[a-z0-9-]+$/.test(normalized)) {
      setError('Only lowercase letters, numbers, and hyphens allowed')
      return
    }
    updateInvestor({ path: { id: investor.id }, body: { slug: normalized } })
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Banner
      variant={variant}
      icon={<Share2 className="size-4.5" />}
      title={isAccepting ? 'Accepting Pitches' : 'Pitches Paused'}
      description={
        isAccepting
          ? 'Founders can discover your profile and pitch to you through this link'
          : 'Your profile is still visible, but founders cannot submit new pitches'
      }
      actions={
        <Switch
          checked={isAccepting}
          onCheckedChange={(checked) =>
            updateInvestor({
              path: { id: investor.id },
              body: { isAcceptingPitches: checked },
            })
          }
          className="data-[state=checked]:bg-emerald-500"
        />
      }
    >
      <div className="space-y-2 px-4 pb-4">
        {error && <ErrorBanner message={error} />}
        <div className="flex items-center gap-2">
          <div
            className={`flex flex-1 items-center h-8 rounded-lg border bg-black/20 ${borderColor} overflow-hidden`}
          >
            <span className="pl-3 text-sm text-white/50 shrink-0 select-none">
              {window.location.origin}/
            </span>
            <input
              ref={inputRef}
              value={slugInput}
              onChange={(e) => {
                setSlugInput(e.target.value)
                setError(null)
              }}
              readOnly={!isEditing}
              className={`flex-1 bg-transparent font-mono text-sm text-white/80 outline-none min-w-0 ${
                !isEditing ? 'cursor-default' : ''
              }`}
            />
            {isEditing ? (
              <button
                type="button"
                onClick={handleSave}
                disabled={isPending || slugInput === investor.slug}
                className="px-3 h-full text-xs font-medium text-white/70 hover:text-white disabled:opacity-40 shrink-0"
              >
                Save
              </button>
            ) : (
              <button
                type="button"
                onClick={handleEdit}
                className="px-3 h-full text-xs font-medium text-white/70 hover:text-white shrink-0"
              >
                Edit
              </button>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className={`shrink-0 ${borderColor}`}
          >
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            {copied ? 'Copied!' : 'Copy'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(`/${investor.slug}`, '_blank')}
            className={`shrink-0 ${borderColor}`}
          >
            <ExternalLink className="size-4" />
            Preview
          </Button>
        </div>
      </div>
    </Banner>
  )
}

function InvestorLinkSetup({ investor }: { investor: InvestorData }) {
  const [slugInput, setSlugInput] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { mutate: updateInvestor, isPending } = useUpdateInvestorMutation({
    onSuccess: () => setError(null),
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to create link')
    },
  })

  useEffect(() => {
    if (investor.name) {
      setSlugInput(
        investor.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
      )
    }
  }, [investor.name])

  const handleCreate = () => {
    const normalized = slugInput.toLowerCase().trim()
    if (normalized.length < 3) {
      setError('Link must be at least 3 characters')
      return
    }
    if (!/^[a-z0-9-]+$/.test(normalized)) {
      setError('Only lowercase letters, numbers, and hyphens allowed')
      return
    }
    updateInvestor({ path: { id: investor.id }, body: { slug: normalized } })
  }

  return (
    <Banner
      variant="success"
      icon={<Share2 className="size-4.5" />}
      title="Create your SeedForge Link"
      description="Choose your unique link and start receiving pitches from founders"
    >
      <div className="space-y-2 px-4 pb-4">
        {error && <ErrorBanner message={error} />}
        <div className="flex items-center gap-2">
          <div className="flex flex-1 items-center h-8 rounded-lg border bg-black/20 border-emerald-500/20 overflow-hidden">
            <span className="pl-3 text-sm text-white/50 shrink-0 select-none">
              {window.location.origin}/
            </span>
            <input
              value={slugInput}
              onChange={(e) => {
                setSlugInput(e.target.value)
                setError(null)
              }}
              placeholder="your-name"
              className="flex-1 bg-transparent font-mono text-sm text-white/80 outline-none min-w-0 placeholder:text-white/30"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCreate}
            disabled={isPending || !slugInput.trim()}
            className="shrink-0 border-emerald-500/20"
          >
            {isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </Banner>
  )
}

function InvestorPitchTimeline({ investor }: { investor: InvestorData }) {
  const {
    data: pitchesResponse,
    isLoading,
    error,
  } = usePitchesQuery({ investorId: investor.id }, { enabled: !!investor.id })
  const pitches = pitchesResponse?.data ?? []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="md" />
      </div>
    )
  }

  if (error) {
    return <ErrorBanner message={error.message || 'Failed to load pitches'} />
  }

  return (
    <div className="space-y-6 py-8">
      <ProfileSection title="Share Link">
        {investor.slug ? (
          <InvestorLinkBanner investor={investor} />
        ) : (
          <InvestorLinkSetup investor={investor} />
        )}
      </ProfileSection>

      {pitches.length === 0 ? (
        <ProfileSection title="Incoming Pitches">
          <EmptyState
            icon={<Mic className="size-12" />}
            title="No pitches yet"
            description={
              investor.slug
                ? 'Share your link with founders to start receiving pitches'
                : 'Create your link to start receiving pitches'
            }
          />
        </ProfileSection>
      ) : (
        <ProfileSection title="Incoming Pitches">
          <InvestorPitchList pitches={pitches} />
        </ProfileSection>
      )}
    </div>
  )
}

function InvestorPitchList({ pitches }: { pitches: PitchSessionData[] }) {
  return (
    <PitchTimeline
      pitches={pitches}
      renderBadges={(session) =>
        session.startupName && (
          <div className="flex items-center gap-2 mt-1.5">
            <Badge variant="secondary" size="sm" className="gap-1">
              <Building2 className="size-3" />
              {session.startupName}
            </Badge>
          </div>
        )
      }
    />
  )
}
